import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { executeInvoiceNullment } from '../../../../../../../../../lib/api/sii';
import CustomButton from '../../../../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../../../../lib/designSystem/CircularProgress';
import NullmentTable from '../NullmentTable';

const NullmentConfirmDialog = (props: any) => {
  const {
    confirmOpen,
    setConfirmOpen,
    selectedInvoices,
    setSelectedInvoices,
    selectedCertificate,
    setUp,
    setFinalizedModal,
    setSuccessInvoices,
    setResultInvoices,
  } = props;

  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [passphrase, setPassphrase] = useState('');
  const [nullments, setNullments] = useState<any[]>([]);
  const [globalDate, setGlobalDate] = useState('');

  useEffect(() => {
    if (confirmOpen && selectedInvoices && selectedInvoices.length > 0) {
      setNullments(selectedInvoices);
      // Establecer la fecha global con la fecha del primer documento
      if (selectedInvoices[0]?.issuedDate) {
        setGlobalDate(selectedInvoices[0].issuedDate);
      }
    }
  }, [confirmOpen, selectedInvoices]);

  const handleChangeGlobalDate = (date: string) => {
    const nullmentsWithNewDate = nullments.map((nullment: any) => ({
      ...nullment,
      issuedDate: date,
    }));
    setNullments(nullmentsWithNewDate);
    setGlobalDate(date);
  };

  const handleExecuteNullment = async () => {
    setLoading(true);
    try {
      const nullmentData = {
        creditNotes: nullments.map((nullment: any) => ({
          businessIdentifier: nullment.businessIdentifier,
          identifier: nullment.stakeholderIdentifier,
          issuedDate: nullment.issuedDate,
          dtesToCancel: [
            {
              referencedDocType: nullment.docType,
              referencedDocFolio: nullment.folio,
              referencedDocDate: nullment.issuedDate,
              nullmentType: nullment.nullmentType,
              gloss: nullment.gloss,
              amount: nullment.amount,
            },
          ],
        })),
        certificateId: selectedCertificate.id,
        passphrase,
      };

      const response = await executeInvoiceNullment(dispatch, nullmentData);

      if (response.status === 'failed') {
        showAlert(dispatch, 'error', response.message);
        setLoading(false);
        return;
      }

      // Manejar errores del array response.error
      const erroredNullments = (response.error || []).map((nullment: any) => ({
        ...nullment,
        status: false,
      }));

      // Manejar éxitos del array response.success
      const successNullments = (response.success || []).map(
        (nullment: any) => ({
          ...nullment,
          status: true,
        }),
      );

      // Actualizar estados para mostrar en el modal de resultados
      setSuccessInvoices(successNullments);
      setResultInvoices([...successNullments, ...erroredNullments]);
      setFinalizedModal(true);

      // Solo actualizar la tabla si hubo éxitos
      if (successNullments.length > 0) {
        await setUp();
      }

      setSelectedInvoices([]);
      setConfirmOpen(false);
    } catch (error) {
      showAlert(
        dispatch,
        'warning',
        'El proceso de anulación está siendo procesado, espera unos minutos para actualizar la página.',
      );
      console.error(error);
    }
    setLoading(false);
  };

  const handleClose = () => {
    setSelectedInvoices([]);
    setConfirmOpen(false);
  };

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <Dialog maxWidth="xl" open={confirmOpen} onClose={handleClose}>
      <DialogTitle>Confirmar anulación de documentos</DialogTitle>
      <DialogContent>
        <div
          style={{
            padding: '10px 0px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <Typography>
              Se realizará la anulación de los documentos seleccionados.
              <strong>{nullments.length}</strong>{' '}
              {nullments.length === 1 ? 'documento' : 'documentos'} para el rut
              empresa <b>{selectedCertificate?.businessIdentifier}</b>
            </Typography>

            <Typography variant="body2" color="text.secondary">
              <strong>Glosa de anulación:</strong> {nullments[0]?.gloss || ''}
            </Typography>
          </div>

          <TextField
            margin="dense"
            label="Fecha de emisión"
            type="date"
            value={globalDate}
            onChange={(e) => handleChangeGlobalDate(e.target.value)}
          />
        </div>

        <div style={{ padding: '10px 0px' }}>
          <NullmentTable nullments={nullments} setNullments={setNullments} />
        </div>

        <div
          style={{
            padding: '10px 0px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography>
            Para la anulación se debe ingresar la contraseña del certificado de{' '}
            <b>
              {selectedCertificate?.taxName} (
              {selectedCertificate?.taxIdentifier})
            </b>
          </Typography>
          <TextField
            margin="dense"
            label="Contraseña del certificado"
            type="password"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <CustomButton color="secondary" onClick={handleClose}>
          Cancelar
        </CustomButton>
        <CustomButton
          disabled={!passphrase.length}
          onClick={handleExecuteNullment}
          color="error"
        >
          Anular documentos
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default NullmentConfirmDialog;
